import * as Sentry from '@sentry/vue'
import type { ConfiguracaoObservabilidade } from '@next/compartilhados/observabilidade/configuracao'
import { contextoDoEvento, sanitizarEvento } from './evento-sentry'

type FabricaTransporte = NonNullable<Sentry.BrowserOptions['transport']>
export type SaidaConsole = (evento: Sentry.Event) => void
type DependenciasTransporte = Readonly<{
  configuracao: ConfiguracaoObservabilidade
  origemAplicacao: string
  estaAtivo: () => boolean
  registrarDescarte: () => void
  saidaConsole?: SaidaConsole | undefined
  enviar?: typeof fetch | undefined
}>

const imprimirEvento: SaidaConsole = evento => {
  // Única saída de console desta infraestrutura; recebe somente representação sanitizada.
  // eslint-disable-next-line no-console
  console.info('[Observabilidade]', evento)
}

/** Escolhe a saída; classificação e limites do relato já passaram pelo adapter. */
export const criarTransporte = (dependencias: DependenciasTransporte): FabricaTransporte => opcoes => {
  const { configuracao, origemAplicacao, estaAtivo, registrarDescarte, saidaConsole = imprimirEvento } = dependencias
  // Console e desativado nem sequer constroem o transporte HTTP.
  const remoto = configuracao.destino === 'apm'
    ? criarTransporteRemoto(opcoes, dependencias)
    : undefined

  return {
    async send(envelope) {
      if (!estaAtivo() || configuracao.destino === 'desativado') return { statusCode: 200 }
      // Somente erros passam. Anexos, feedback, sessões, traces e logs não saem nesta entrega.
      for (const [cabecalho, dados] of envelope[1]) {
        if (cabecalho.type !== 'event' || !dados || typeof dados !== 'object') continue
        try {
          const evento = dados as Sentry.Event
          // Última fronteira: remove também campos acrescentados pelo SDK após beforeSend.
          // Reutiliza a mesma política; não classifica nem consome o orçamento novamente.
          const seguro = sanitizarEvento(evento, contextoDoEvento(evento), origemAplicacao)
          if (!seguro) { registrarDescarte(); continue }
          seguro.event_id ??= crypto.randomUUID().replace(/-/g, '')
          seguro.environment = configuracao.ambiente
          seguro.release = configuracao.release
          if (configuracao.destino === 'console') saidaConsole(seguro)
          else if (remoto) {
            try {
              await remoto.send([{ event_id: seguro.event_id, sent_at: new Date().toISOString() }, [[{ type: 'event' }, seguro]]])
            } catch { /* O callback do transporte já contabiliza perdas de rede/timeout. */ }
          }
        } catch {
          registrarDescarte()
        }
      }
      return { statusCode: 200 }
    },
    async flush(timeout) {
      try { return remoto ? await remoto.flush(timeout) : true } catch { return false }
    },
  }
}

/** Detalhes do protocolo Sentry/HTTP, isolados do caminho de preparação dos erros. */
const criarTransporteRemoto = (
  opcoes: Parameters<FabricaTransporte>[0],
  { estaAtivo, registrarDescarte, enviar = (...argumentos) => fetch(...argumentos) }: DependenciasTransporte,
) => Sentry.createTransport({
  ...opcoes,
  bufferSize: 10,
  recordDroppedEvent: (_motivo, categoria, quantidade = 1) => {
    if (categoria !== 'error') return
    for (let indice = 0; indice < quantidade; indice++) registrarDescarte()
  },
}, async requisicao => {
  if (!estaAtivo()) return { statusCode: 200 }
  const controlador = new AbortController()
  const timeout = setTimeout(() => { controlador.abort() }, 3000)
  try {
    const resposta = await enviar(opcoes.url, {
      method: 'POST', body: requisicao.body as BodyInit,
      credentials: 'omit', referrerPolicy: 'no-referrer', signal: controlador.signal,
    })
    // O SDK já conta 413 pelo callback acima; as demais rejeições são contadas aqui.
    if ((resposta.status < 200 || resposta.status >= 300) && resposta.status !== 413) registrarDescarte()
    return {
      statusCode: resposta.status,
      headers: {
        'retry-after': resposta.headers.get('retry-after'),
        'x-sentry-rate-limits': resposta.headers.get('x-sentry-rate-limits'),
      },
    }
  } finally {
    clearTimeout(timeout)
  }
})
